import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import graphql from 'api/graphql'
import { selectCurrentUser } from 'ducks/currentUser/selectors/selectCurrentUser'
import { isEmpty } from 'util/objects'
import {
  createHelplyIdentifyTokenProvider,
  listenForHelplyOpen,
  setHelplyIdentify,
} from 'util/helplyWidget'
import useHelplyWidgetEnabled from 'util/hooks/useHelplyWidgetEnabled'
import { selectCurrentBillingCycle } from 'ducks/billing/selectors/selectCurrentBillingCycle'

// One provider for the page: the widget keeps the function it was given and calls it whenever it needs a
// token, so it must not change identity between renders.
const fetchHelplyIdentifyToken = createHelplyIdentifyTokenProvider(graphql)

// Not awaited: before the widget boots the stub returns undefined, and commands never reject. Email and name
// travel inside the signed token; they are reserved keys as attributes. The provider resolves null when the
// environment has no widget secret, and the identify is then anonymous: attributes only.
const identifyHelplyAgent = (currentUser, billingCycle) => {
  if (typeof window.Helply !== 'function') return
  window.Helply('identify', {
    token: fetchHelplyIdentifyToken,
    attributes: {
      'Unified-Inbox': currentUser.preferences.unified_inbox,
      'Billing-Cycle': billingCycle,
    },
  })
}

/**
 * Opens support chat, by what `useHelplyWidgetEnabled` says this account gets. The new Helply widget:
 * `Helply('open')`, and every open identifies the agent (`setHelplyIdentify` in `util/helplyWidget`), the
 * Help button's and the widget's own when a reminder email's link opens a conversation — with a token
 * PROVIDER: the widget fetches a fresh signed token when it sends the identify, and again whenever that token
 * runs out, so an agent who keeps Groove open all day stays verified (Groove signs the token for two hours) —
 * plus the unverified attributes. Each identify replaces the widget session. Otherwise: the Groove widget.
 */
const useOpenContact = ({ left } = {}) => {
  const currentUser = useSelector(selectCurrentUser)
  const billingCycle = useSelector(selectCurrentBillingCycle)
  const helply = useHelplyWidgetEnabled()

  // Kept, not cleared on unmount: several components mount this hook, and the identify reads the same user.
  useEffect(
    () => {
      if (!helply.widget || isEmpty(currentUser)) return
      setHelplyIdentify(() => identifyHelplyAgent(currentUser, billingCycle))
    },
    [currentUser, helply.widget, billingCycle]
  )

  const openContact = useCallback(
    async e => {
      if (helply.widget && typeof window.Helply === 'function') {
        // The open listener identifies; registered again in case the widget reloaded its bundle and dropped it.
        listenForHelplyOpen()
        window.Helply('open')
        if (e?.preventDefault) {
          e.preventDefault()
        }

        return
      }
      if (!window.groove?.widget) return
      if (e?.preventDefault) {
        e.preventDefault()
      }

      if (left) {
        await window.groove.widget.updateSettings({
          iconPosition: 'left',
        })
      }
      window.groove.widget.pushRoute('/contact')
      window.groove.widget.open()
      if (!isEmpty(currentUser)) {
        window.groove.widget.identifyContact('contact_email', currentUser.email)
        window.groove.widget.setContact({
          contact_first_name: currentUser.first_name,
          contact_last_name: currentUser.last_name,
        })
      }
    },
    [currentUser, left, helply]
  )

  return openContact
}

export default useOpenContact
